import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import ModuleTour from '../components/Training/ModuleTour';
import { useIsMobile } from '../hooks/useIsMobile';

const PreConsultations = () => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [creatingRequest, setCreatingRequest] = useState(false);
  const [schedulingRequestId, setSchedulingRequestId] = useState(null);
  const [viewMode, setViewMode] = useState(() => window.innerWidth <= 768 ? 'grid' : 'kanban'); // 'kanban' | 'grid' | 'ai_appointments'
  const [dateMode, setDateMode] = useState('today'); // 'today' | 'week' | 'month' | 'all' | 'range'
  // 'range': iki tarih arası (eskiden "Tarih Seç" sadece TEK bir günü seçtiriyordu)
  const [rangeFrom, setRangeFrom] = useState(() => toISODate(new Date()));
  const [rangeTo, setRangeTo] = useState(() => toISODate(new Date()));
  // Arama her tuşta değil, yazmayı bırakınca (400 ms) sunucuya gider.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Sayfalama: sunucu toplam kayıt sayısını döner, "Daha fazla yükle" sonraki sayfayı ekler.
  const PAGE_SIZE = 50;
  const [pagination, setPagination] = useState({ total: 0, has_more: false });
  const [loadingMore, setLoadingMore] = useState(false);
  const [aiAppointments, setAiAppointments] = useState([]);
  const [aiAppointmentsLoading, setAiAppointmentsLoading] = useState(false);
  const [aiAppointmentActionId, setAiAppointmentActionId] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [showCallbackPicker, setShowCallbackPicker] = useState(false);
  const [callbackDateInput, setCallbackDateInput] = useState('');
  const [savingCallback, setSavingCallback] = useState(false);
  const [editingCallerName, setEditingCallerName] = useState(false);
  const [callerNameDraft, setCallerNameDraft] = useState('');
  const [savingCallerName, setSavingCallerName] = useState(false);

  function toISODate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // <input type="datetime-local">'ın beklediği "YYYY-MM-DDTHH:mm" biçimi (saniyesiz).
  function toDatetimeLocalValue(d) {
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${toISODate(d)}T${h}:${min}`;
  }

  // Backend'den gelen callback_date artık DATETIME - mysql2'nin özel typeCast'i (bkz.
  // db/connection.js) bunu zaten "YYYY-MM-DDTHH:mm:ss" düz metin olarak döndürüyor
  // (Z eki yok, saat dilimi riski yok) - datetime-local input'u için sadece saniyeyi kesiyoruz.
  function toDatetimeLocalInput(mysqlDatetimeStr) {
    if (!mysqlDatetimeStr) return '';
    return String(mysqlDatetimeStr).replace(' ', 'T').slice(0, 16);
  }

  // datetime-local input'unun değerini ("YYYY-MM-DDTHH:mm") backend'e göndermeden önce
  // MySQL'in kendi DATETIME string biçimine ("YYYY-MM-DD HH:mm:00") çeviriyor.
  function toMySQLDatetime(datetimeLocalValue) {
    if (!datetimeLocalValue) return null;
    return datetimeLocalValue.replace('T', ' ') + ':00';
  }

  const getDateRange = () => {
    const now = new Date();
    if (dateMode === 'today') {
      const d = toISODate(now);
      return { date_from: d, date_to: d };
    }
    if (dateMode === 'week') {
      const day = now.getDay(); // 0 = Sunday
      const diffToMonday = (day === 0 ? -6 : 1) - day;
      const monday = new Date(now);
      monday.setDate(now.getDate() + diffToMonday);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { date_from: toISODate(monday), date_to: toISODate(sunday) };
    }
    if (dateMode === 'month') {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { date_from: toISODate(first), date_to: toISODate(last) };
    }
    // Tüm zamanlar: tarih filtresi yok
    if (dateMode === 'all') return null;
    // range - ters girilmişse (bitiş < başlangıç) sessizce yer değiştir
    const from = rangeFrom || rangeTo;
    const to = rangeTo || rangeFrom;
    return from <= to ? { date_from: from, date_to: to } : { date_from: to, date_to: from };
  };

  // Arama yapılırken tarih aralığı UYGULANMAZ - eski bir müşteriyi bulmak için
  // önce doğru tarih aralığını tahmin etmek gerekmesin.
  const isSearching = debouncedSearch.trim().length > 0;
  const effectiveDateRange = () => (isSearching ? null : getDateRange());

  // "Son N gün" kısayolları - tarih aralığı modunu doldurur.
  const setLastDays = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - (days - 1));
    setRangeFrom(toISODate(from));
    setRangeTo(toISODate(to));
    setDateMode('range');
  };

  const formatISODateLabel = (iso) => {
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  };

  const formatDateRangeLabel = () => {
    if (isSearching) return '🔎 Tüm geçmişte aranıyor';
    const range = getDateRange();
    if (!range) return 'Tüm zamanlar';
    const { date_from, date_to } = range;
    return date_from === date_to
      ? formatISODateLabel(date_from)
      : `${formatISODateLabel(date_from)} – ${formatISODateLabel(date_to)}`;
  };

  const COLUMNS = [
    { id: 'pending', label: 'Bekliyor', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.05)' },
    { id: 'contacted', label: 'Arandı', color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.05)' },
    { id: 'no_response', label: 'Cevap Yok', color: '#6B7280', bg: 'rgba(107, 114, 128, 0.05)' },
    { id: 'callback_later', label: 'Tekrar Aranacak', color: '#F97316', bg: 'rgba(249, 115, 22, 0.05)' },
    { id: 'scheduled', label: 'Randevulu', color: '#10B981', bg: 'rgba(16, 185, 129, 0.05)' },
    { id: 'completed', label: 'Tamamlandı', color: '#6366F1', bg: 'rgba(99, 102, 241, 0.05)' },
    { id: 'lost', label: 'Kayıp', color: '#EF4444', bg: 'rgba(239, 68, 68, 0.05)' }
  ];

  const handleDrop = async (e, targetStatus) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('requestId');
    if (!id) return;
    // "Tekrar Aranacak" sürükle-bırakla da tek tıkla ayarlanamaz - backend hangi gün
    // aranacağı olmadan bu durumu reddediyor (bkz. PUT /:id). Direkt kaydetmek yerine
    // detay panelini tarih seçiciyle açık şekilde gösteriyoruz.
    if (targetStatus === 'callback_later') {
      const req = requests.find(r => String(r.id) === String(id));
      if (req) {
        openPickerOnSelectRef.current = true;
        setSelectedRequest(req);
      }
      return;
    }
    await updateStatus(id, targetStatus);
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Filtre değişince ilk sayfadan başla.
  useEffect(() => { fetchRequests({ reset: true }); }, [filter, debouncedSearch, dateMode, rangeFrom, rangeTo]);

  // AI Randevuları sekmesi yalnızca seçiliyken çekilir — gereksiz her filtre
  // değişiminde ayrı bir uca istek atmaktan kaçınmak için.
  useEffect(() => {
    if (viewMode === 'ai_appointments') fetchAiAppointments();
  }, [viewMode, dateMode, rangeFrom, rangeTo]);

  const fetchAiAppointments = async () => {
    setAiAppointmentsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('source', 'ai_agent');
      const range = getDateRange();
      if (range) {
        params.append('start_date', range.date_from);
        params.append('end_date', range.date_to);
      }

      const res = await fetch(`/api/appointments?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` }
      });
      const data = await res.json();
      setAiAppointments(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); setAiAppointments([]); }
    setAiAppointmentsLoading(false);
  };

  const updateAiAppointmentStatus = async (id, status) => {
    setAiAppointmentActionId(id);
    try {
      await fetch(`/api/appointments/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
        body: JSON.stringify({ status })
      });
      await fetchAiAppointments();
    } catch (err) { console.error(err); }
    setAiAppointmentActionId(null);
  };

  const cancelAiAppointment = async (id) => {
    if (!confirm('Bu AI randevusu iptal edilsin mi?')) return;
    setAiAppointmentActionId(id);
    try {
      await fetch(`/api/appointments/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
        body: JSON.stringify({ cancelled_by: 'salon' })
      });
      await fetchAiAppointments();
    } catch (err) { console.error(err); }
    setAiAppointmentActionId(null);
  };

  const getAppointmentStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', border: '1px solid rgba(245, 158, 11, 0.3)', text: 'Bekliyor' },
      confirmed: { bg: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)', text: 'Onaylandı' },
      checked_in: { bg: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.3)', text: 'Geldi' },
      in_progress: { bg: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.3)', text: 'İşlemde' },
      completed: { bg: 'rgba(16, 185, 129, 0.15)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.3)', text: 'Tamamlandı' },
      cancelled_by_customer: { bg: 'rgba(239, 68, 68, 0.15)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)', text: 'İptal' },
      cancelled_by_salon: { bg: 'rgba(239, 68, 68, 0.15)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)', text: 'İptal' },
      no_show: { bg: 'rgba(107, 114, 128, 0.15)', color: '#9ca3af', border: '1px solid rgba(107, 114, 128, 0.3)', text: 'Gelmedi' }
    };
    const b = badges[status] || { bg: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)', border: '1px solid var(--border)', text: status };
    return <span style={{ background: b.bg, color: b.color, border: b.border, padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>{b.text}</span>;
  };

  const buildRequestParams = () => {
    const params = new URLSearchParams();
    if (filter !== 'all') params.append('status', filter);
    if (debouncedSearch.trim()) params.append('search', debouncedSearch.trim());
    const range = effectiveDateRange();
    if (range) {
      params.append('date_from', range.date_from);
      params.append('date_to', range.date_to);
    }
    return params;
  };

  // reset: filtre değişti, ilk sayfayı getir. Aksi halde (bir kayıt güncellendikten
  // sonraki yenileme) şu an yüklü olan kadar kaydı yeniden getirir - "Daha fazla
  // yükle" ile açılmış sayfalar her durum değişikliğinde kapanmasın.
  const loadedCountRef = useRef(0);
  const fetchRequests = async ({ reset = false } = {}) => {
    setLoading(true);
    try {
      const params = buildRequestParams();
      const limit = reset ? PAGE_SIZE : Math.min(Math.max(loadedCountRef.current, PAGE_SIZE), 200);
      params.append('limit', limit);
      params.append('offset', 0);

      const res = await fetch(`/api/pre-consultations?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` }
      });
      const data = await res.json();
      const list = data.requests || [];
      setRequests(list);
      loadedCountRef.current = list.length;
      setStats(data.stats || {});
      setPagination(data.pagination || { total: list.length, has_more: false });
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadMoreRequests = async () => {
    setLoadingMore(true);
    try {
      const params = buildRequestParams();
      params.append('limit', PAGE_SIZE);
      params.append('offset', requests.length);
      const res = await fetch(`/api/pre-consultations?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` }
      });
      const data = await res.json();
      const more = data.requests || [];
      setRequests(prev => {
        const seen = new Set(prev.map(r => r.id));
        const merged = [...prev, ...more.filter(r => !seen.has(r.id))];
        loadedCountRef.current = merged.length;
        return merged;
      });
      if (data.pagination) setPagination(data.pagination);
      if (data.stats) setStats(data.stats);
    } catch (err) { console.error(err); }
    setLoadingMore(false);
  };

  const formatDate = (date) => date ? new Date(date).toLocaleString('tr-TR') : '-';

  const getCountryFlag = (code) => {
    const flags = { TR: '🇹🇷', US: '🇺🇸', DE: '🇩🇪', GB: '🇬🇧', SA: '🇸🇦', FR: '🇫🇷', IT: '🇮🇹', NL: '🇳🇱' };
    return flags[code] || '🌍';
  };

  // Instagram konuşmalarında AI bazen "phone" alanına gerçek Instagram-scoped
  // gönderen ID'sini (tipik olarak 15+ haneli) yazıyor, bazen müşterinin sohbet
  // sırasında verdiği GERÇEK telefon numarasını (tipik 10-13 hane). İkisi aynı
  // kolonda tutulduğu için önceden hep "Instagram ID" etiketleniyordu - müşteri
  // numara verse bile personel bunu göremiyor, sadece serbest metin notlarda
  // arıyordu. Hane sayısına göre ayırt edip gerçek numaraysa "Telefon" + aranabilir
  // link gösteriyoruz.
  const looksLikePhoneNumber = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    return digits.length > 0 && digits.length <= 13;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', border: '1px solid rgba(245, 158, 11, 0.3)', text: 'Bekliyor' },
      contacted: { bg: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)', text: 'Arandı' },
      no_response: { bg: 'rgba(107, 114, 128, 0.15)', color: '#9ca3af', border: '1px solid rgba(107, 114, 128, 0.3)', text: 'Cevap Yok' },
      callback_later: { bg: 'rgba(249, 115, 22, 0.15)', color: '#fb923c', border: '1px solid rgba(249, 115, 22, 0.3)', text: 'Tekrar Aranacak' },
      scheduled: { bg: 'rgba(16, 185, 129, 0.15)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.3)', text: 'Randevulu' },
      completed: { bg: 'rgba(99, 102, 241, 0.15)', color: '#a78bfa', border: '1px solid rgba(99, 102, 241, 0.3)', text: 'Tamamlandı' },
      lost: { bg: 'rgba(239, 68, 68, 0.15)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)', text: 'Kayıp' }
    };
    const b = badges[status] || { bg: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)', border: '1px solid var(--border)', text: status };
    return <span style={{ background: b.bg, color: b.color, border: b.border, padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>{b.text}</span>;
  };

  const getSourceIcon = (source) => {
    const icons = { call: '📞', whatsapp: '💬', instagram: '📸', web: '🌐' };
    return icons[source] || '📋';
  };

  // Talebin kaynagina gore musteri kaydinda tutulacak source degeri - customers.source
  // ENUM'u appointments.source'tan farkli (orn. 'pre_consultation' degeri yok), bu yuzden
  // en yakin karsiligi kullaniyoruz.
  const SOURCE_TO_CUSTOMER_SOURCE = { call: 'phone', whatsapp: 'whatsapp', instagram: 'instagram', web: 'web' };
  const normalizePhone = (p) => String(p || '').replace(/\D/g, '').slice(-10);

  // callback_date backend'den düz "YYYY-MM-DDTHH:mm:ss" metin olarak geliyor (DATETIME,
  // mysql2'nin typeCast'i sayesinde Z eki yok - bkz. db/connection.js). ISO metin
  // karşılaştırması kronolojik sıralamayla birebir aynı olduğu için "gecikmiş mi" sorusu
  // Date nesnesine çevirmeden (saat dilimi riski taşımadan) yanıtlanabiliyor.
  const callbackBadge = (dateStr) => {
    if (!dateStr) return null;
    // Gün karşılaştırması saat kısmından ETKİLENMİYOR - "2026-08-17T.." < "2026-08-18"
    // string sıralamasında saat basamağına hiç ulaşmadan gün basamağında karar veriyor,
    // yani "bugün daha geç bir saate" ayarlanmış bir kayıt asla "gecikti" sayılmıyor.
    const overdue = dateStr < toISODate(new Date());
    const label = new Date(dateStr).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    return { overdue, label };
  };

  // Telefonla var olan musteriyi arar (LIKE bulanik arama sonuclari arasindan SON 10
  // hanesi birebir eslesen TEK kaydi seçer - yanlislikla baska bir musteriyi
  // baglamamak icin), yoksa talebin ad/telefon bilgisiyle yeni musteri olusturur.
  const resolveOrCreateCustomer = async (request) => {
    const phone = (request.phone || request.caller_phone || '').trim();
    if (!phone) return null; // Instagram vb. telefonsuz talep - otomatik olusturulamaz

    const token = localStorage.getItem('crm_token');
    const headers = { Authorization: `Bearer ${token}` };
    const targetDigits = normalizePhone(phone);

    try {
      const res = await fetch(`/api/customers?search=${encodeURIComponent(phone)}&limit=20`, { headers });
      if (res.ok) {
        const data = await res.json();
        const match = (data.data || []).find(c => normalizePhone(c.phone) === targetDigits);
        if (match) return match.id;
      }
    } catch (e) { /* arama basarisiz olursa yeni musteri olusturmaya devam edilir */ }

    const parts = (request.caller_name || '').trim().split(/\s+/).filter(Boolean);
    const first_name = parts.length > 1 ? parts.slice(0, -1).join(' ') : (parts[0] || 'Ön Görüşme Müşterisi');
    const last_name = parts.length > 1 ? parts[parts.length - 1] : '';

    const createRes = await fetch('/api/customers', {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ first_name, last_name, phone, source: SOURCE_TO_CUSTOMER_SOURCE[request.source] || 'other' })
    });
    if (!createRes.ok) throw new Error('Müşteri oluşturulamadı');
    const created = await createRes.json();
    return created.id;
  };

  // "Randevulandı": musteriyi otomatik bulup/olusturup takvime gider, randevu formunu
  // talep bilgileriyle VE secili musteriyle onceden doldurur (elle arama/ekleme
  // gerekmez). Talebin durumu, randevu gercekten kaydedildiginde Calendar tarafindan
  // 'scheduled' yapilir.
  const goToCalendar = async (request) => {
    setSchedulingRequestId(request.id);
    let customerId = request.customer_id || null;
    if (!customerId) {
      try {
        customerId = await resolveOrCreateCustomer(request);
      } catch (e) {
        console.error('Customer resolve/create error:', e);
        alert('Müşteri otomatik oluşturulamadı, randevu ekranında elle seçin/ekleyin.');
      }
    }
    setSchedulingRequestId(null);
    navigate('/calendar', {
      state: {
        preConsultation: {
          id: request.id,
          customer_id: customerId,
          caller_name: request.caller_name || '',
          phone: request.phone || request.caller_phone || '',
          preferred_date: request.preferred_date || null,
          notes: request.notes || request.ai_summary || ''
        }
      }
    });
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await fetch(`/api/pre-consultations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
        body: JSON.stringify({ status: newStatus })
      });
      fetchRequests();
      setSelectedRequest(null);
    } catch (err) { console.error(err); }
  };

  // Notlar alanı eskiden defaultValue (uncontrolled) ile sadece İLK açılıştaki metni
  // gösteriyordu - hiçbir onChange/kaydet mekanizması olmadığı için yazılan hiçbir şey
  // kaydedilmiyordu ("notlar güncellenmiyor" şikayetinin kök nedeni). selectedRequest
  // değişince (farklı bir talep açılınca) taslağı O talebin notlarıyla sıfırlıyoruz.
  // Kanban'da bir kartı "Tekrar Aranacak" sütununa sürükleyince (bkz. handleDrop) bu
  // ref'i işaretleyip detay panelini açıyoruz - aşağıdaki efekt normalde panel her
  // açıldığında tarih seçiciyi KAPATIR, ama bu durumda TAM TERSİNİ istiyoruz (seçici
  // zaten açık gelsin). Aynı state güncellemesinde hem "kapat" hem "aç" çağrılsaydı
  // React'ın batch'lemesi yüzünden efekt her zaman kazanıp seçiciyi anında kapatırdı.
  const openPickerOnSelectRef = useRef(false);
  useEffect(() => {
    setNotesDraft(selectedRequest?.notes || '');
    setNotesSaved(false);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0);
    setCallbackDateInput(toDatetimeLocalInput(selectedRequest?.callback_date) || toDatetimeLocalValue(tomorrow));
    setCallerNameDraft(selectedRequest?.caller_name || '');
    setEditingCallerName(false);
    if (openPickerOnSelectRef.current) {
      openPickerOnSelectRef.current = false;
      setShowCallbackPicker(true);
    } else {
      setShowCallbackPicker(false);
    }
  }, [selectedRequest?.id]);

  const saveCallerName = async () => {
    if (!selectedRequest || !callerNameDraft.trim()) return;
    setSavingCallerName(true);
    try {
      await fetch(`/api/pre-consultations/${selectedRequest.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
        body: JSON.stringify({ caller_name: callerNameDraft.trim() })
      });
      setRequests(prev => prev.map(r => r.id === selectedRequest.id ? { ...r, caller_name: callerNameDraft.trim() } : r));
      setSelectedRequest(prev => prev && { ...prev, caller_name: callerNameDraft.trim() });
      setEditingCallerName(false);
    } catch (err) {
      console.error(err);
      alert('İsim kaydedilemedi.');
    } finally {
      setSavingCallerName(false);
    }
  };

  // "🔁 Tekrar Ara": diğer durum butonlarının aksine tek tıkla değil, önce hangi gün
  // aranacağını sormalı (bkz. kullanıcı talebi) - status ve callback_date AYNI PUT
  // isteğinde birlikte gönderiliyor, backend ikisi birlikte gelmezse 400 döndürüyor
  // (callback_date'siz "callback_later" durumu, tarih filtresinde asla görünmeyen bir
  // talep demek olurdu).
  const saveCallbackLater = async () => {
    if (!selectedRequest || !callbackDateInput) return;
    setSavingCallback(true);
    try {
      const res = await fetch(`/api/pre-consultations/${selectedRequest.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
        body: JSON.stringify({ status: 'callback_later', callback_date: toMySQLDatetime(callbackDateInput) })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kaydedilemedi');
      }
      setShowCallbackPicker(false);
      fetchRequests();
      setSelectedRequest(null);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Kaydedilemedi.');
    } finally {
      setSavingCallback(false);
    }
  };

  const saveNotes = async () => {
    if (!selectedRequest) return;
    setSavingNotes(true);
    try {
      await fetch(`/api/pre-consultations/${selectedRequest.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
        body: JSON.stringify({ notes: notesDraft })
      });
      setRequests(prev => prev.map(r => r.id === selectedRequest.id ? { ...r, notes: notesDraft } : r));
      setSelectedRequest(prev => prev && { ...prev, notes: notesDraft });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    } catch (err) {
      console.error(err);
      alert('Not kaydedilemedi.');
    } finally {
      setSavingNotes(false);
    }
  };

  const deleteRequest = async (id) => {
    if (!confirm('Silmek istediğinizden emin misiniz?')) return;
    try {
      await fetch(`/api/pre-consultations/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` }
      });
      fetchRequests();
      setSelectedRequest(null);
    } catch (err) { console.error(err); }
  };

  const createRequest = async (e) => {
    e.preventDefault();
    if (creatingRequest) return; // çift tıklamada ikinci isteği hiç yollama
    setCreatingRequest(true);
    const form = e.target;
    const data = {
      caller_name: form.name.value,
      phone: form.phone.value,
      country_code: form.country.value,
      sector: form.sector.value,
      notes: form.notes.value,
      source: form.source.value
    };
    
    try {
      await fetch('/api/pre-consultations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
        body: JSON.stringify(data)
      });
      setShowModal(false);
      fetchRequests();
      form.reset();
    } catch (err) { console.error(err); }
    setCreatingRequest(false);
  };

  const getSuggestedService = () => {
    if (!selectedRequest?.suggested_appointment) return null;
    try {
      return JSON.parse(selectedRequest.suggested_appointment);
    } catch { return null; }
  };

  const suggested = getSuggestedService();

  return (
    <div className="preconsultations-page">
      <ModuleTour moduleKey="preConsultations" />
      <div className="page-header" data-tour="pc-header" style={{ flexWrap: 'wrap', gap: '12px', alignItems: isMobile ? 'stretch' : 'center' }}>
        <div>
          <h1 className="page-title">📋 Ön Görüşme Talepleri</h1>
          <p className="page-subtitle">AI ve kanallardan gelen ön görüşme talepleri</p>
        </div>
        <button className="btn btn-primary" data-tour="pc-new-btn" onClick={() => setShowModal(true)}>
          + Yeni Talep
        </button>
      </div>

      <div className="stats-cards" data-tour="pc-stats">
        <div className={`stat-card ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, var(--tertiary), var(--secondary))' }}>📋</div>
          <div className="stat-content">
            <div className="stat-value">{stats.total || 0}</div>
            <div className="stat-label">Toplam</div>
          </div>
        </div>
        <div className={`stat-card ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #F59E0B, #FBBF24)' }}>⏳</div>
          <div className="stat-content">
            <div className="stat-value">{stats.pending || 0}</div>
            <div className="stat-label">Bekliyor</div>
          </div>
        </div>
        <div className={`stat-card ${filter === 'contacted' ? 'active' : ''}`} onClick={() => setFilter('contacted')}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #3B82F6, #60A5FA)' }}>📞</div>
          <div className="stat-content">
            <div className="stat-value">{stats.contacted || 0}</div>
            <div className="stat-label">Arandı</div>
          </div>
        </div>
        <div className={`stat-card ${filter === 'no_response' ? 'active' : ''}`} onClick={() => setFilter('no_response')}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #6B7280, #9CA3AF)' }}>🔕</div>
          <div className="stat-content">
            <div className="stat-value">{stats.no_response || 0}</div>
            <div className="stat-label">Cevap Yok</div>
          </div>
        </div>
        <div className={`stat-card ${filter === 'callback_later' ? 'active' : ''}`} onClick={() => setFilter('callback_later')}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #F97316, #FB923C)' }}>🔁</div>
          <div className="stat-content">
            <div className="stat-value">{stats.callback_later || 0}</div>
            <div className="stat-label">Tekrar Aranacak</div>
          </div>
        </div>
        <div className={`stat-card ${filter === 'scheduled' ? 'active' : ''}`} onClick={() => setFilter('scheduled')}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10B981, #34D399)' }}>📅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.scheduled || 0}</div>
            <div className="stat-label">Randevulu</div>
          </div>
        </div>
        <div className={`stat-card ${filter === 'completed' ? 'active' : ''}`} onClick={() => setFilter('completed')}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, var(--tertiary), #A78BFA)' }}>✓</div>
          <div className="stat-content">
            <div className="stat-value">{stats.completed || 0}</div>
            <div className="stat-label">Tamamlandı</div>
          </div>
        </div>
        <div className={`stat-card ${filter === 'lost' ? 'active' : ''}`} onClick={() => setFilter('lost')}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #EF4444, #F87171)' }}>✕</div>
          <div className="stat-content">
            <div className="stat-value">{stats.lost || 0}</div>
            <div className="stat-label">Kayıp</div>
          </div>
        </div>
      </div>

      <div className="filters-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '12px', flex: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            className="input"
            data-tour="pc-search"
            placeholder="İsim, telefon, not, hizmet ara (tüm geçmiş)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: '320px' }}
          />
          {search && (
            <button className="btn btn-sm btn-ghost" onClick={() => setSearch('')} title="Aramayı temizle" style={{ padding: '6px 10px', marginLeft: '-8px' }}>✕</button>
          )}
          <div className="date-mode-toggle" data-tour="pc-date-filter" style={{ display: 'flex', background: 'rgba(255, 255, 255, 0.03)', padding: '4px', borderRadius: '8px', border: '1px solid var(--border)', opacity: isSearching ? 0.5 : 1 }} title={isSearching ? 'Arama yapılırken tarih filtresi uygulanmaz' : undefined}>
            {[
              { id: 'today', label: 'Bugün' },
              { id: 'week', label: 'Bu Hafta' },
              { id: 'month', label: 'Bu Ay' },
              { id: 'all', label: 'Tüm Zamanlar' },
              { id: 'range', label: '📅 Tarih Aralığı' },
            ].map(opt => (
              <button
                key={opt.id}
                className={`btn btn-sm ${dateMode === opt.id ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setDateMode(opt.id)}
                style={{ padding: '6px 12px', borderRadius: '6px' }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {dateMode === 'range' && !isSearching && (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="date"
                className="input"
                value={rangeFrom}
                max={rangeTo || undefined}
                onChange={(e) => setRangeFrom(e.target.value)}
                style={{ maxWidth: '160px' }}
                aria-label="Başlangıç tarihi"
              />
              <span style={{ color: 'var(--text-muted)' }}>–</span>
              <input
                type="date"
                className="input"
                value={rangeTo}
                min={rangeFrom || undefined}
                onChange={(e) => setRangeTo(e.target.value)}
                style={{ maxWidth: '160px' }}
                aria-label="Bitiş tarihi"
              />
              {[{ d: 7, l: 'Son 7 gün' }, { d: 30, l: 'Son 30 gün' }, { d: 90, l: 'Son 3 ay' }].map(s => (
                <button key={s.d} className="btn btn-sm btn-ghost" onClick={() => setLastDays(s.d)} style={{ padding: '6px 10px', fontSize: '12px' }}>
                  {s.l}
                </button>
              ))}
            </div>
          )}
          <span style={{ fontSize: '13px', color: isSearching ? 'var(--primary)' : 'var(--text-muted)' }}>{formatDateRangeLabel()}</span>
          <button className="btn btn-secondary" onClick={() => fetchRequests()}>🔄 Yenile</button>
        </div>
        <div className="view-mode-toggle" data-tour="pc-view-toggle" style={{ display: 'flex', background: 'rgba(255, 255, 255, 0.03)', padding: '4px', borderRadius: '8px', border: '1px solid var(--border)' }}>
          <button
            className={`btn btn-sm ${viewMode === 'kanban' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setViewMode('kanban')}
            style={{ padding: '6px 12px', borderRadius: '6px' }}
          >
            📋 Pipeline
          </button>
          <button
            className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setViewMode('grid')}
            style={{ padding: '6px 12px', borderRadius: '6px' }}
          >
            ☰ Liste
          </button>
          <button
            className={`btn btn-sm ${viewMode === 'ai_appointments' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setViewMode('ai_appointments')}
            style={{ padding: '6px 12px', borderRadius: '6px' }}
          >
            🤖 AI Randevuları
          </button>
        </div>
      </div>

      {viewMode === 'ai_appointments' ? (
        <div className="ai-appointments-section" data-tour="pc-ai-appointments">
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '14px' }}>
            AI ajanının doğrudan oluşturduğu randevular — yeni oluşan bir randevu <strong style={{ color: '#fbbf24' }}>Bekliyor</strong> durumunda açılır, gözden geçirip onaylayın veya iptal edin.
          </div>
          {aiAppointmentsLoading ? (
            <div className="loading"><div className="loading-spinner"></div></div>
          ) : aiAppointments.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
              Bu tarih aralığında AI'ın oluşturduğu randevu yok.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {aiAppointments.map(apt => (
                <div
                  key={apt.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 16px',
                    borderRadius: '12px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)',
                    borderLeft: `4px solid ${apt.status === 'pending' ? '#fbbf24' : apt.color_code || '#6366f1'}`
                  }}
                >
                  <div style={{ minWidth: '110px' }}>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text)' }}>
                      {new Date(apt.scheduled_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {new Date(apt.scheduled_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {apt.customer_first_name} {apt.customer_last_name}
                    </div>
                    <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {apt.service_name || apt.product_name || '-'}
                      {apt.staff_first_name && ` · ${apt.staff_first_name} ${apt.staff_last_name}`}
                    </div>
                  </div>
                  {getAppointmentStatusBadge(apt.status)}
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    {apt.status === 'pending' && (
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={aiAppointmentActionId === apt.id}
                        onClick={() => updateAiAppointmentStatus(apt.id, 'confirmed')}
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                      >
                        ✅ Onayla
                      </button>
                    )}
                    {!['cancelled_by_customer', 'cancelled_by_salon', 'completed'].includes(apt.status) && (
                      <button
                        className="btn btn-sm btn-ghost"
                        disabled={aiAppointmentActionId === apt.id}
                        onClick={() => cancelAiAppointment(apt.id)}
                        style={{ padding: '6px 12px', fontSize: '12px', color: '#f87171' }}
                      >
                        ✕ İptal
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : viewMode === 'kanban' ? (
        <div className="kanban-board" data-tour="pc-kanban">
          {COLUMNS.map(col => {
            const colRequests = requests.filter(r => r.status === col.id);
            return (
              <div 
                key={col.id} 
                className="kanban-column"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, col.id)}
                style={{ background: col.bg, borderTop: `4px solid ${col.color}` }}
              >
                <div className="kanban-column-header">
                  <span style={{ color: col.color, fontWeight: '700' }}>{col.label}</span>
                  {/* Sayfalı yüklemede sütunda henüz yüklenmemiş kayıtlar olabilir - gerçek toplamı göster */}
                  {(() => {
                    const partial = (filter === 'all' || filter === col.id) && (stats[col.id] || 0) > colRequests.length;
                    return (
                      <span className="kanban-column-count" title={partial ? `${stats[col.id]} kayıttan ${colRequests.length} tanesi yüklü - aşağıdan "Daha fazla yükle"` : undefined}>
                        {partial ? `${colRequests.length}/${stats[col.id]}` : colRequests.length}
                      </span>
                    );
                  })()}
                </div>
                <div className="kanban-column-body">
                  {colRequests.map(req => (
                    <div 
                      key={req.id} 
                      className="kanban-card" 
                      draggable 
                      onDragStart={(e) => e.dataTransfer.setData('requestId', req.id)}
                      onClick={() => setSelectedRequest(req)}
                    >
                      <div className="request-header" style={{ marginBottom: '8px' }}>
                        <div className="request-flags">
                          <span>{getCountryFlag(req.country_code)}</span>
                          <span>{getSourceIcon(req.source)}</span>
                          {req.account_label && (
                            <span title="Bağlı hesap" style={{ fontSize: '9px', fontWeight: 600, color: '#60a5fa', background: 'rgba(96,165,250,0.15)', borderRadius: '999px', padding: '1px 7px' }}>
                              {req.account_label}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="request-name" style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>{req.caller_name}</div>
                      <div className="request-phone" style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '2px 0' }}>{req.phone}</div>
                      <div className="request-sector" style={{ fontSize: '11px', padding: '2px 8px', margin: '4px 0 8px' }}>{req.sector}</div>
                      {req.status === 'callback_later' && req.callback_date && (() => {
                        const cb = callbackBadge(req.callback_date);
                        return (
                          <div style={{ fontSize: '11px', fontWeight: 600, color: cb.overdue ? '#f87171' : '#fb923c', margin: '4px 0' }}>
                            🔁 {cb.overdue ? 'Gecikti: ' : ''}{cb.label}
                          </div>
                        );
                      })()}
                      {req.ai_summary && (
                        <div className="request-summary" style={{ fontSize: '11px', padding: '8px', borderRadius: '6px', background: 'rgba(var(--primary-rgb), 0.08)', color: 'var(--primary)', border: '1px solid rgba(var(--primary-rgb), 0.15)', marginTop: '6px' }}>
                          <span className="ai-badge" style={{ padding: '1px 4px', fontSize: '9px', background: 'var(--primary)', color: 'var(--bg)' }}>🤖 AI</span>
                          {req.ai_summary.substring(0, 50)}...
                        </div>
                      )}
                    </div>
                  ))}
                  {colRequests.length === 0 && (
                    <div className="kanban-empty">Talep yok</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="requests-grid">
          {loading ? (
            <div className="loading">Yükleniyor...</div>
          ) : requests.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <div className="empty-title">{isSearching ? `"${debouncedSearch.trim()}" ile eşleşen talep bulunamadı` : 'Talep bulunamadı'}</div>
              {!isSearching && dateMode !== 'all' && (
                <button className="btn btn-sm btn-ghost" onClick={() => setDateMode('all')} style={{ marginTop: '10px' }}>Tüm zamanlarda göster</button>
              )}
            </div>
          ) : (
            requests.map(req => (
              <div key={req.id} className="request-card" onClick={() => setSelectedRequest(req)}>
                <div className="request-header">
                  <div className="request-flags">
                    <span>{getCountryFlag(req.country_code)}</span>
                    <span>{getSourceIcon(req.source)}</span>
                    {req.account_label && (
                      <span title="Bağlı hesap" style={{ fontSize: '9px', fontWeight: 600, color: '#60a5fa', background: 'rgba(96,165,250,0.15)', borderRadius: '999px', padding: '1px 7px' }}>
                        {req.account_label}
                      </span>
                    )}
                  </div>
                  {getStatusBadge(req.status)}
                </div>
                <div className="request-name">{req.caller_name}</div>
                <div className="request-phone">{req.phone}</div>
                <div className="request-sector">{req.sector}</div>
                {req.status === 'callback_later' && req.callback_date && (() => {
                  const cb = callbackBadge(req.callback_date);
                  return (
                    <div style={{ fontSize: '12px', fontWeight: 600, color: cb.overdue ? '#f87171' : '#fb923c', margin: '4px 0' }}>
                      🔁 {cb.overdue ? 'Gecikti: ' : ''}{cb.label} tarihinde aranacak
                    </div>
                  );
                })()}
                {req.ai_summary && (
                  <div className="request-summary">
                    <span className="ai-badge">🤖 AI</span>
                    {req.ai_summary.substring(0, 80)}...
                  </div>
                )}
                <div className="request-footer">
                  <span>{formatDate(req.created_at)}</span>
                  {req.assigned_staff_name && <span>👤 {req.assigned_staff_name}</span>}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {viewMode !== 'ai_appointments' && !loading && pagination.total > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', margin: '20px 0 8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            {pagination.total} kayıttan {requests.length} tanesi gösteriliyor
          </span>
          {pagination.has_more && (
            <button className="btn btn-secondary" onClick={loadMoreRequests} disabled={loadingMore}>
              {loadingMore ? 'Yükleniyor...' : `Daha fazla yükle (${Math.min(PAGE_SIZE, pagination.total - requests.length)})`}
            </button>
          )}
        </div>
      )}

      {selectedRequest && (
        <div className="modal-overlay" onClick={() => setSelectedRequest(null)} style={isMobile ? { alignItems: 'flex-end', padding: 0 } : {}}>
          <div className="modal large" onClick={(e) => e.stopPropagation()} style={isMobile ? { borderRadius: '20px 20px 0 0', maxHeight: '92dvh', overflowY: 'auto', width: '100%', maxWidth: '100%', margin: 0 } : {}}>
            <div className="modal-header">
              <h2>Ön Görüşme Detayı</h2>
              <button className="modal-close" onClick={() => setSelectedRequest(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Müşteri</label>
                  {editingCallerName ? (
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <input
                        type="text"
                        className="input"
                        style={{ padding: '4px 8px', fontSize: '14px' }}
                        value={callerNameDraft}
                        onChange={(e) => setCallerNameDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveCallerName(); if (e.key === 'Escape') setEditingCallerName(false); }}
                        autoFocus
                      />
                      <button className="btn btn-sm btn-secondary" onClick={saveCallerName} disabled={savingCallerName || !callerNameDraft.trim()}>
                        {savingCallerName ? '⏳' : '✓'}
                      </button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setEditingCallerName(false)}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{getCountryFlag(selectedRequest.country_code)} {selectedRequest.caller_name}</span>
                      <button
                        className="btn btn-sm btn-secondary"
                        style={{ padding: '2px 8px', fontSize: '11px' }}
                        onClick={() => { setCallerNameDraft(selectedRequest.caller_name || ''); setEditingCallerName(true); }}
                      >
                        ✏️ Düzenle
                      </button>
                    </div>
                  )}
                </div>
                <div className="detail-item">
                  <label>{selectedRequest.source === 'instagram' && !looksLikePhoneNumber(selectedRequest.phone) ? 'Instagram ID' : 'Telefon'}</label>
                  <div>
                    {looksLikePhoneNumber(selectedRequest.phone) ? (
                      <a href={`tel:${selectedRequest.phone}`} style={{ color: 'inherit' }}>{selectedRequest.phone}</a>
                    ) : selectedRequest.phone}
                  </div>
                </div>
                <div className="detail-item">
                  <label>Sektör</label>
                  <div>{selectedRequest.sector}</div>
                </div>
                <div className="detail-item">
                  <label>Durum</label>
                  {getStatusBadge(selectedRequest.status)}
                </div>
                <div className="detail-item">
                  <label>Kaynak</label>
                  <div>{getSourceIcon(selectedRequest.source)} {selectedRequest.source}{selectedRequest.account_label ? ` · ${selectedRequest.account_label}` : ''}</div>
                </div>
                <div className="detail-item">
                  <label>Oluşturulma</label>
                  <div>{formatDate(selectedRequest.created_at)}</div>
                </div>
              </div>

              {selectedRequest.ai_summary && (
                <div className="ai-summary-box">
                  <label>🤖 AI Özet</label>
                  <div>{selectedRequest.ai_summary}</div>
                </div>
              )}

              {suggested && (
                <div className="suggestion-box">
                  <label>💡 AI Randevu Önerisi</label>
                  <div className="suggestion-content">
                    <div><strong>Hizmet:</strong> {suggested.service}</div>
                    <div><strong>Personel:</strong> {suggested.staff}</div>
                    <div><strong>Süre:</strong> {suggested.estimated_duration} dk</div>
                    <div><strong>Fiyat:</strong> ₺{suggested.estimated_price?.toLocaleString()}</div>
                  </div>
                  <button className="btn btn-success" onClick={() => goToCalendar(selectedRequest)} disabled={schedulingRequestId === selectedRequest.id}>
                    {schedulingRequestId === selectedRequest.id ? '⏳ Müşteri hazırlanıyor...' : '📅 Randevuya Dönüştür'}
                  </button>
                </div>
              )}

              <div className="notes-section">
                <label>Notlar</label>
                <textarea
                  className="textarea"
                  value={notesDraft}
                  onChange={(e) => { setNotesDraft(e.target.value); setNotesSaved(false); }}
                  placeholder="Not ekle..."
                  rows="3"
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={saveNotes}
                    disabled={savingNotes || notesDraft === (selectedRequest.notes || '')}
                  >
                    {savingNotes ? '⏳ Kaydediliyor...' : '💾 Notu Kaydet'}
                  </button>
                  {notesSaved && <span style={{ color: 'var(--success, #22c55e)', fontSize: '13px', fontWeight: 600 }}>✓ Kaydedildi</span>}
                </div>
              </div>

              <div className="actions-section">
                <label>İşlemler</label>
                <div className="action-buttons">
                  <button className="btn btn-warning" onClick={() => updateStatus(selectedRequest.id, 'contacted')}>
                    📞 Arandı
                  </button>
                  <button className="btn btn-secondary" onClick={() => updateStatus(selectedRequest.id, 'no_response')}>
                    🔕 Cevap Yok
                  </button>
                  <button
                    className="btn"
                    style={{ background: 'rgba(249, 115, 22, 0.15)', color: '#fb923c', border: '1px solid rgba(249, 115, 22, 0.3)' }}
                    onClick={() => setShowCallbackPicker(v => !v)}
                  >
                    🔁 Tekrar Ara
                  </button>
                  <button className="btn btn-success" onClick={() => goToCalendar(selectedRequest)} disabled={schedulingRequestId === selectedRequest.id}>
                    {schedulingRequestId === selectedRequest.id ? '⏳ Müşteri hazırlanıyor...' : '📅 Randevulandı'}
                  </button>
                  <button className="btn btn-primary" onClick={() => updateStatus(selectedRequest.id, 'completed')}>
                    ✓ Tamamlandı
                  </button>
                  <button className="btn btn-danger" onClick={() => updateStatus(selectedRequest.id, 'lost')}>
                    ✕ Kayıp
                  </button>
                  <button className="btn btn-danger btn-outline" onClick={() => deleteRequest(selectedRequest.id)}>
                    🗑️ Sil
                  </button>
                </div>

                {showCallbackPicker && (
                  <div style={{ marginTop: '12px', padding: '12px', borderRadius: '8px', background: 'rgba(249, 115, 22, 0.08)', border: '1px solid rgba(249, 115, 22, 0.25)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '13px', color: '#fb923c', fontWeight: 600 }}>Hangi gün/saat tekrar aransın?</span>
                    <input
                      type="datetime-local"
                      className="input"
                      style={{ width: 'auto' }}
                      value={callbackDateInput}
                      min={toDatetimeLocalValue(new Date())}
                      onChange={(e) => setCallbackDateInput(e.target.value)}
                    />
                    <button className="btn btn-warning" onClick={saveCallbackLater} disabled={savingCallback || !callbackDateInput}>
                      {savingCallback ? '⏳ Kaydediliyor...' : '✓ Onayla'}
                    </button>
                    <button className="btn btn-secondary" onClick={() => setShowCallbackPicker(false)}>İptal</button>
                  </div>
                )}
                {selectedRequest.status === 'callback_later' && selectedRequest.callback_date && !showCallbackPicker && (
                  <div style={{ marginTop: '10px', fontSize: '13px', color: '#fb923c' }}>
                    🔁 {new Date(selectedRequest.callback_date).toLocaleString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })} tarihinde tekrar aranacak
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)} style={isMobile ? { alignItems: 'flex-end', padding: 0 } : {}}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={isMobile ? { borderRadius: '20px 20px 0 0', maxHeight: '92dvh', overflowY: 'auto', width: '100%', maxWidth: '100%', margin: 0 } : {}}>
            <div className="modal-header">
              <h2>Yeni Ön Görüşme Talebi</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={createRequest}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Ad Soyad</label>
                  <input className="input" name="name" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefon</label>
                  <input className="input" name="phone" required placeholder="+90..." />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Ülke</label>
                    <select className="select" name="country">
                      <option value="TR">Türkiye</option>
                      <option value="DE">Almanya</option>
                      <option value="GB">İngiltere</option>
                      <option value="FR">Fransa</option>
                      <option value="IT">İtalya</option>
                      <option value="NL">Hollanda</option>
                      <option value="SA">Suudi Arabistan</option>
                      <option value="US">ABD</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Kaynak</label>
                    <select className="select" name="source">
                      <option value="call">Telefon</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="instagram">Instagram</option>
                      <option value="web">Web</option>
                      <option value="manual">Manuel</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Sektör</label>
                  <input className="input" name="sector" placeholder="Saç Ekimi, Diş Tedavi..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Notlar</label>
                  <textarea className="textarea" name="notes" rows="3" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>İptal</button>
                <button type="submit" className="btn btn-primary" disabled={creatingRequest}>
                  {creatingRequest ? 'Oluşturuluyor...' : 'Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .preconsultations-page {
          padding: 24px;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          color: var(--text);
          margin: 0;
        }

        .page-subtitle {
          color: var(--text-muted);
          margin: 4px 0 0 0;
        }

        .stats-cards {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        .stat-card {
          background: var(--bg-card);
          border-radius: var(--radius-xl);
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          cursor: pointer;
          transition: var(--transition-fast);
          border: 1px solid var(--border);
        }

        .stat-card:hover {
          transform: translateY(-2px);
          background: var(--bg-card-hover);
          border-color: var(--border-hover);
          box-shadow: var(--glow-blue-subtle);
        }

        .stat-card.active {
          border-color: var(--primary);
          background: rgba(var(--primary-rgb), 0.05);
          box-shadow: 0 0 15px rgba(var(--primary-rgb), 0.1);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          color: var(--bg);
        }

        .stat-value {
          font-size: 24px;
          font-weight: 700;
          color: var(--text);
        }

        .stat-label {
          font-size: 13px;
          color: var(--text-muted);
        }

        .filters-bar {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
        }

        .filters-bar .input {
          max-width: 300px;
        }

        .requests-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
        }

        .request-card {
          background: var(--bg-card);
          border-radius: var(--radius-xl);
          padding: 20px;
          cursor: pointer;
          transition: var(--transition-fast);
          border: 1px solid var(--border);
        }

        .request-card:hover {
          background: var(--bg-card-hover);
          border-color: var(--primary);
          box-shadow: var(--glow-blue-subtle);
        }

        .request-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .request-flags {
          display: flex;
          gap: 8px;
        }

        .request-name {
          font-weight: 700;
          font-size: 16px;
          color: var(--text);
          margin-bottom: 4px;
        }

        .request-phone {
          color: var(--text-muted);
          font-size: 14px;
          margin-bottom: 8px;
        }

        .request-sector {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid var(--border);
          color: var(--text-muted);
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 12px;
          display: inline-block;
          margin-bottom: 12px;
        }

        .request-summary {
          font-size: 13px;
          color: var(--primary);
          background: rgba(var(--primary-rgb), 0.08);
          border: 1px solid rgba(var(--primary-rgb), 0.15);
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 12px;
        }

        .ai-badge {
          background: var(--primary);
          color: var(--bg);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 700;
          margin-right: 6px;
        }

        .request-footer {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: var(--text-muted);
        }

        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(3, 4, 7, 0.85);
          backdrop-filter: blur(12px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: var(--bg-card);
          border: 1px solid var(--border-hover);
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-xl);
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow: hidden;
        }

        .modal.large {
          max-width: 700px;
        }

        .modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid var(--border);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h2 {
          margin: 0;
          font-weight: 700;
          color: var(--text);
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 28px;
          cursor: pointer;
          color: var(--text-muted);
          transition: var(--transition-fast);
        }

        .modal-close:hover {
          color: var(--danger);
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
          max-height: 60vh;
        }

        .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid var(--border);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          background: rgba(26, 26, 36, 0.4);
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 20px;
        }

        .detail-item label {
          font-size: 12px;
          color: var(--text-muted);
          display: block;
          margin-bottom: 4px;
        }

        .detail-item > div {
          font-weight: 600;
          color: var(--text);
        }

        .ai-summary-box {
          background: rgba(var(--primary-rgb), 0.08);
          border: 1px solid rgba(var(--primary-rgb), 0.15);
          padding: 16px;
          border-radius: 12px;
          margin-bottom: 20px;
        }

        .ai-summary-box label {
          font-size: 14px;
          color: var(--primary);
          font-weight: 700;
          display: block;
          margin-bottom: 8px;
        }

        .ai-summary-box div {
          color: var(--text);
        }

        .suggestion-box {
          background: rgba(16, 185, 129, 0.08);
          border: 1px solid rgba(16, 185, 129, 0.15);
          padding: 16px;
          border-radius: 12px;
          margin-bottom: 20px;
        }

        .suggestion-box label {
          font-size: 14px;
          color: var(--emerald);
          font-weight: 700;
          display: block;
          margin-bottom: 12px;
        }

        .suggestion-content {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
          margin-bottom: 16px;
          color: var(--text);
        }

        .notes-section {
          margin-bottom: 20px;
        }

        .notes-section label {
          font-size: 14px;
          color: var(--text);
          font-weight: 700;
          display: block;
          margin-bottom: 8px;
        }

        .actions-section label {
          font-size: 14px;
          color: var(--text);
          font-weight: 700;
          display: block;
          margin-bottom: 12px;
        }

        .action-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .form-row {
          display: flex;
          gap: 16px;
        }

        .form-row .form-group {
          flex: 1;
        }

        .kanban-board {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 16px;
          align-items: start;
          margin-top: 8px;
          min-height: 500px;
          overflow-x: auto;
        }

        .kanban-column {
          background: rgba(255, 255, 255, 0.01);
          border: 1px solid var(--border);
          border-radius: var(--radius-xl);
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          box-shadow: var(--shadow-sm);
          min-height: 450px;
          transition: var(--transition-fast);
        }

        .kanban-column-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border);
        }

        .kanban-column-count {
          background: rgba(255, 255, 255, 0.05);
          color: var(--text-muted);
          font-size: 12px;
          padding: 2px 8px;
          border-radius: 20px;
          font-weight: 600;
        }

        .kanban-column-body {
          display: flex;
          flex-direction: column;
          gap: 12px;
          flex: 1;
        }

        .kanban-card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: 16px;
          cursor: grab;
          box-shadow: var(--shadow-sm);
          transition: var(--transition-fast);
        }

        .kanban-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary);
          background: var(--bg-card-hover);
        }

        .kanban-card:active {
          cursor: grabbing;
        }

        .kanban-empty {
          text-align: center;
          color: var(--text-muted);
          font-size: 13px;
          padding: 20px 0;
          border: 2px dashed var(--border);
          border-radius: 12px;
        }

        .loading {
          text-align: center;
          padding: 60px;
          color: var(--text-muted);
        }

        .empty-state {
          text-align: center;
          padding: 60px;
          color: var(--text-muted);
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .empty-title {
          font-size: 18px;
          font-weight: 700;
          color: var(--text);
        }

        @media (max-width: 1024px) {
          .kanban-board {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 768px) {
          .preconsultations-page {
            padding: 12px;
          }
          .page-title {
            font-size: 20px;
          }
          .page-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
            margin-bottom: 16px;
          }
          .stats-cards {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
            margin-bottom: 16px;
          }
          .stat-card {
            padding: 12px;
            gap: 10px;
          }
          .stat-icon {
            width: 36px;
            height: 36px;
            font-size: 16px;
          }
          .stat-value {
            font-size: 18px;
          }
          .stat-label {
            font-size: 11px;
          }
          .filters-bar {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            margin-bottom: 16px;
          }
          .filters-bar .input {
            max-width: 100% !important;
            width: 100%;
          }
          .date-mode-toggle, .view-mode-toggle {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            width: 100%;
            justify-content: flex-start;
          }
          .date-mode-toggle button, .view-mode-toggle button {
            white-space: nowrap;
            font-size: 12px;
            padding: 6px 10px;
          }
          .kanban-board {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          .requests-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          .request-card {
            padding: 14px;
          }
          .detail-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
        }

        @media (max-width: 480px) {
          .stats-cards {
            grid-template-columns: 1fr 1fr;
            gap: 6px;
          }
        }
      `}</style>
    </div>
  );
};

export default PreConsultations;
